import React from "react";
import {
  pricingColumns,
  pricingRows,
} from "client-server-shared/pricing/pricing-table";
import { Box } from "components/ui/box";
import { Typography } from "components/ui/Typography";
import { Paper, PaperProps } from "components/ui/paper";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "components/ui/table";
import { Tooltip } from "components/ui/tooltip";
import { useTranslation } from "components/translate";
import { usePricingContext } from "./pricing";
import { ResponsiveText } from "modules/marketing-pages/front-page/shared";
import { colors } from "src/constants";
import { IconComponent } from "components/icon-component";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const BlackCheckMark = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="17"
      viewBox="0 0 19 17"
      fill="none"
    >
      <path
        d="M2 9.36507C3.59668 9.36507 8.68026 18.8941 9.41621 12.6384C9.9372 8.21008 12.5978 3.65294 16.7301 2"
        stroke={colors.red1}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
};

const RedCross = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="17"
      viewBox="0 0 19 17"
      fill="none"
    >
      <path
        d="M17 1L1 17M1 1L17 17"
        stroke={colors.gray2}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
};

const PricingTableMobile = () => {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {columns
          .filter((column) => column.label)
          .map((column) => (
            <Typography
              key={column.id}
              style={{
                fontSize: "16px",
                fontWeight: 500,
                border: 0,
              }}
            >
              {column.label}
            </Typography>
          ))}
      </Box>
    </Box>
  );
};

const PricingTable = ({ sx = {}, hideHeading }: PaperProps) => {
  const { t } = useTranslation("pricing");
  const { customValue } = usePricingContext();
  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: "none",
        border: 0,
        ...sx,
      }}
    >
      {hideHeading ? null : (
        <ResponsiveText
          sx={{
            textAlign: "center",
            padding: "48px 0",
            color: colors.black1,
          }}
          component="h2"
          fontSize={36}
        >
          Compare plans
        </ResponsiveText>
      )}
      <TableContainer
        sx={{
          border: hideHeading ? 0 : `1px solid ${colors.gray2}`,
          borderRadius: "10px",
        }}
      >
        <Table stickyHeader aria-label="pricing table" id="pricing-table">
          <TableHead>
            <TableRow>
              {pricingColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    width: {
                      xs: `${column.minWidth / 2}px`,
                      sm: `${column.minWidth / 2}px`,
                      md: `${column.minWidth}px`,
                      lg: `${column.minWidth}px`,
                    },
                    fontSize: "24px",
                    fontWeight: 500,
                    color: colors.black1,
                  }}
                >
                  {t(column.label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pricingRows({ customValue }).map((row) => {
              if (row.category) {
                return (
                  <TableRow key={row.category}>
                    <TableCell
                      sx={{
                        border: 0,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: colors.black1,
                        }}
                      >
                        {t(row.category)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                <TableRow
                  sx={{
                    // hide last border
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                  }}
                  hover
                  key={row.feature.label}
                >
                  {pricingColumns.map((column, index) => {
                    const value = row[column.id];
                    const { check, description, info, label } = value;
                    return (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          padding: {
                            xs: "6px 0 6px 16px",
                            sm: "6px 0 6px 16px",
                            md: "16px",
                            lg: "16px",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent:
                              column.align === "center"
                                ? "center"
                                : "flex-start",
                          }}
                        >
                          {typeof check !== "undefined" ? (
                            check ? (
                              <BlackCheckMark />
                            ) : (
                              <RedCross />
                            )
                          ) : null}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {value.label ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  columnGap: "4px",
                                }}
                              >
                                <Tooltip
                                  title={info ? t(info) : ""}
                                  placement="right"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: colors.black1,
                                      fontSize: "16px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {t(value.label)}
                                  </Typography>
                                </Tooltip>
                                {info && (
                                  <Tooltip title={info ? t(info) : ""}>
                                    <IconComponent
                                      icon={InfoOutlinedIcon}
                                      sx={{
                                        height: "14px",
                                        width: "14px",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            ) : null}
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PricingTable;
